/**
 * Legacy-Static-Objects (Control-Points/Bases + Titan-Silos) für die Live-TAC-Map.
 *
 * Quelle: `assets/static_objects.json` (aus legacy-php importiert). Struktur:
 *   mapName -> variant (z.B. `gpm_cq_64`, `gpm_ti_48`) -> [ { n, t, x, z, team, base } ]
 * `x/z` sind WELT-Koordinaten (gleiche Basis wie Spielerpositionen) und werden
 * über die bestehende Kalibrier-Projektion (`mapWorldToTacPoint`) in normalisierte
 * TAC-Koordinaten (0..1) gebracht. Reine, DOM-freie Helfer — testbar.
 *
 * Wichtig: KEINE Fake-Daten. Titane (mobil) haben keine statische Position und
 * werden hier NICHT erzeugt. Manuelle `tac_maps.json`-Punkte haben immer Vorrang.
 */

import type { TacPoint, TacPointKind, TacPointTeam } from "./points";

/** Roh-Objekt aus static_objects.json. */
export interface RawStaticObject {
  n?: string;
  t?: string;
  x?: number;
  z?: number;
  team?: number;
  base?: boolean;
}

/** variant -> Objekte. */
export type StaticObjectVariants = Record<string, RawStaticObject[]>;
/** mapName -> variants. */
export type StaticObjectData = Record<string, StaticObjectVariants>;

/** Projektion Welt(x,z) -> normalisiert(0..1) oder null (out of bounds / keine Calib). */
export type ProjectFn = (calib: unknown, world: { x: number; z: number }) => { x: number; y: number } | null;

const SILO_ICON_COUNT = 5;

export function normalizeMapKey(name: string | null | undefined): string {
  return String(name ?? "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}

/** team-Code -> TacPointTeam (1=PAC, 2=EU, sonst neutral). */
export function teamName(team: number | undefined): TacPointTeam {
  return team === 1 ? "pac" : team === 2 ? "eu" : "neutral";
}

/** Parst/sanitisiert die gefetchte JSON defensiv in die typisierte Struktur. */
export function parseStaticObjects(raw: unknown): StaticObjectData {
  if (!raw || typeof raw !== "object") return {};
  const out: StaticObjectData = {};
  for (const [map, variants] of Object.entries(raw as Record<string, unknown>)) {
    if (!variants || typeof variants !== "object") continue;
    const vOut: StaticObjectVariants = {};
    for (const [variant, objs] of Object.entries(variants as Record<string, unknown>)) {
      if (!Array.isArray(objs)) continue;
      vOut[variant] = objs.filter((o): o is RawStaticObject => !!o && typeof o === "object");
    }
    out[normalizeMapKey(map)] = vOut;
  }
  return out;
}

/** Variants der aktuellen Map (per normalisiertem Namen). */
export function staticObjectsForMap(data: StaticObjectData, mapName: string | null | undefined): StaticObjectVariants | null {
  const key = normalizeMapKey(mapName);
  if (!key) return null;
  return data[key] ?? null;
}

/**
 * Wählt die passende Variante: exakt `${mode}_${size}`, sonst erste Variante mit
 * Prefix `${mode}_` (alphabetisch stabil), sonst null. `mode` z.B. `gpm_ti`.
 */
export function pickStaticObjectVariant(
  variants: StaticObjectVariants | null | undefined,
  mode: string | null | undefined,
  size: number | null | undefined,
): string | null {
  if (!variants) return null;
  const keys = Object.keys(variants);
  if (keys.length === 0) return null;
  const m = String(mode ?? "").trim().toLowerCase();
  if (!m) return null;
  const exact = `${m}_${Math.trunc(Number(size) || 0)}`;
  if (keys.includes(exact)) return exact;
  const prefixed = keys.filter((k) => k.toLowerCase().startsWith(`${m}_`)).sort();
  return prefixed[0] ?? null;
}

/**
 * Kind aus dem Roh-Objekt. Im Titan-Modus (`gpm_ti_*`, Fallback ohne kuratierte
 * Quelle) ist der `base:true`-Control-Point der **Titan** und der Rest **Silo**
 * (Parität zum alten UI, app.js `_cps`-Pfad). Sonst base→base, MissileSilo→silo,
 * sonst flag.
 */
function kindOf(obj: RawStaticObject, variant: string): TacPointKind {
  if (variant.toLowerCase().startsWith("gpm_ti")) {
    return obj.base ? "titan" : "silo";
  }
  if (obj.base) return "base";
  if (/missilesilo/i.test(String(obj.n ?? "")) || /missilesilo/i.test(String(obj.t ?? ""))) return "silo";
  return "flag";
}

/**
 * Icon-Pfad für einen static Punkt (Legacy-Asset). Silos: feste Nummer wenn
 * `siloNumber` (1..5) gegeben, sonst stabiler Index. Titan: einheitliches
 * `mini_titan` (Team wird über Farbe/Klasse gezeigt, wie im alten UI).
 */
export function staticIconPath(kind: TacPointKind, team: TacPointTeam, siloIndex = 0): string | null {
  if (kind === "titan") return `/assets/vehicles/mini_titan.png`;
  if (kind === "silo") {
    const n = String((((siloIndex % SILO_ICON_COUNT) + SILO_ICON_COUNT) % SILO_ICON_COUNT) + 1).padStart(2, "0");
    return `/assets/vehicles/mini_titanSilo_${n}.png`;
  }
  if (kind === "flag") return `/assets/vehicles/flag_${team}.png`;
  if (kind === "base") return `/assets/vehicles/base_${team}.png`;
  return null;
}

/**
 * Projiziert die Objekte einer Variante in TAC-Punkte. `project` = die bestehende
 * `mapWorldToTacPoint`-Logik (injiziert, damit der Helfer DOM-/render-frei bleibt).
 * Out-of-bounds / keine Kalibrierung -> Punkt wird übersprungen. Silos bekommen
 * stabile Icon-Nummern (sortiert nach Welt-x/z, NICHT nach Render-Reihenfolge).
 */
export function staticObjectsToTacPoints(
  objects: RawStaticObject[] | null | undefined,
  calib: unknown,
  variant: string,
  project: ProjectFn,
): TacPoint[] {
  if (!Array.isArray(objects) || objects.length === 0) return [];

  // Stabile Silo-Reihenfolge: nach Welt-x, dann -z (unabhängig von Projektion/Array-Order).
  const siloOrder = new Map<RawStaticObject, number>();
  objects
    .filter((o) => kindOf(o, variant) === "silo")
    .sort((a, b) => (Number(a.x) || 0) - (Number(b.x) || 0) || (Number(a.z) || 0) - (Number(b.z) || 0))
    .forEach((o, i) => siloOrder.set(o, i));

  const out: TacPoint[] = [];
  objects.forEach((obj, i) => {
    const world = { x: Number(obj.x) || 0, z: Number(obj.z) || 0 };
    const xy = project(calib, world);
    if (!xy) return; // keine Calib / out of bounds -> sauber übersprungen
    const kind = kindOf(obj, variant);
    const team = teamName(obj.team);
    const label = String(obj.n ?? obj.t ?? "Objekt").trim() || "Objekt";
    const siloIndex = kind === "silo" ? siloOrder.get(obj) ?? 0 : 0;
    const icon = staticIconPath(kind, team, siloIndex) ?? undefined;
    out.push({
      id: `static-${variant}-${i}`,
      label,
      kind,
      x: xy.x,
      y: xy.y,
      team,
      flagName: label,
      order: i,
      enabled: true,
      ...(icon ? { icon } : {}),
    });
  });
  return out;
}

function roundKey(p: TacPoint): string {
  return `${p.kind}:${p.x.toFixed(2)}:${p.y.toFixed(2)}`;
}

function labelKey(p: TacPoint): string {
  return String(p.flagName ?? p.label ?? "").trim().toLowerCase();
}

/**
 * Merge mit Manual-Vorrang: erst alle manuellen Punkte (Reihenfolge erhalten),
 * dann static Punkte nur, wenn KEIN manueller Punkt kollidiert. Kollision =
 * gleicher kind + gerundete Position (2 Dezimalstellen) ODER gleicher Label/
 * flagName (case-insensitive). Static wird stabil nach order/label sortiert.
 */
export function mergeTacPoints(manual: TacPoint[], staticPts: TacPoint[]): TacPoint[] {
  const manualList = Array.isArray(manual) ? manual : [];
  const staticList = Array.isArray(staticPts) ? staticPts : [];

  const posKeys = new Set(manualList.map(roundKey));
  const labelKeys = new Set(manualList.map(labelKey).filter((k) => k !== ""));

  const accepted = staticList
    .filter((s) => !posKeys.has(roundKey(s)) && !labelKeys.has(labelKey(s)))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.label.localeCompare(b.label));

  return [...manualList, ...accepted];
}
